import type { HTMLAttributes, ReactNode } from 'react'

import { Button } from '../../actions/button/Button'
import { useTranslate } from '../../hooks/useTranslate'
import { Modal } from './Modal'

type AcceptConfiguration = {
  label?: string
  disabled?: boolean
  loading?: boolean
} & ({ onAccept: () => void } | { formId: string })

type Props = Omit<HTMLAttributes<HTMLElement>, 'title'> & {
  isOpen: boolean
  title: ReactNode
  children: ReactNode
  closeConfiguration: {
    label?: string
    onClose: () => void | Promise<void>
  }
  acceptConfiguration?: AcceptConfiguration
  bodyInnerClassName?: string
  bodyOuterClassName?: string
  SecondaryFooterAction?: ReactNode
}

export const SimpleModal = ({
  isOpen,
  title,
  children,
  closeConfiguration,
  acceptConfiguration,
  bodyInnerClassName,
  bodyOuterClassName,
  SecondaryFooterAction,
  ...restProps
}: Props) => {
  const translate = useTranslate()

  return (
    <Modal {...restProps} isOpen={isOpen} onClose={closeConfiguration.onClose}>
      <Modal.Title title={title} />
      <Modal.Body
        outerClassName={bodyOuterClassName}
        innerClassName={bodyInnerClassName}
      >
        {children}
      </Modal.Body>
      <Modal.Footer>
        {SecondaryFooterAction && (
          <div className="grow">{SecondaryFooterAction}</div>
        )}
        <Button
          variant={
            // should be primary if it is the only button
            SecondaryFooterAction || acceptConfiguration
              ? 'discreet'
              : 'primary'
          }
          onClick={closeConfiguration.onClose}
        >
          {closeConfiguration.label ?? translate('close')}
        </Button>
        {acceptConfiguration && (
          <Button
            variant="primary"
            type={'formId' in acceptConfiguration ? 'submit' : 'button'}
            onClick={
              'onAccept' in acceptConfiguration
                ? acceptConfiguration.onAccept
                : undefined
            }
            form={
              'formId' in acceptConfiguration
                ? acceptConfiguration.formId
                : undefined
            }
            disabled={!!acceptConfiguration.disabled}
            isLoading={!!acceptConfiguration.loading}
          >
            {acceptConfiguration.label ?? translate('apply')}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
